import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, getDocs, collection, query, where } from 'firebase/firestore';

import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function EditarAfiliado() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [afiliado, setAfiliado] = useState<any>(null);
  const [redes, setRedes] = useState({ facebook: '', instagram: '', twitter: '', linkedin: '' });
  const [propiedades, setPropiedades] = useState<any[]>([]);


useEffect(() => {
  if (!id) return;

  const fetchData = async () => {
    try {
      const snap = await getDoc(doc(db, 'asesor', id));

      if (!snap.exists()) {
        Swal.fire({
          icon: 'error',
          title: 'No encontrado',
          text: 'El afiliado no existe.'
        });
        navigate('/admin-afiliado');
        return;
      }

      const data = snap.data();
      setAfiliado({ id: snap.id, ...data });
      setRedes(data.redes || {});

      // ✅ Buscar propiedades usando asesor_doc_ids (doc.id de Firestore)
      try {
        const qProps = query(
          collection(db, 'propiedades'),
          where('asesor_doc_ids', 'array-contains', id)
        );
        const propsSnap = await getDocs(qProps);
        const asociadas = propsSnap.docs.map(p => ({ id: p.id, ...p.data() }));
        setPropiedades(asociadas);
        console.log(`✅ Propiedades encontradas para asesor ${data.nombre}:`, asociadas.length);
      } catch (error) {
        console.error('Error cargando propiedades:', error);
        setPropiedades([]);
      }
    } catch (error) {
      console.error('Error al cargar afiliado:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar el afiliado.'
      });
    }
  };

  fetchData();
}, [id, navigate]);



  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAfiliado((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleRedSocialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRedes(prev => ({ ...prev, [name]: value }));
  };

  const validarCampos = () => {
    // Validar campos obligatorios
    if (!afiliado?.nombre?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Campo requerido', text: 'El nombre es obligatorio.' });
      return false;
    }

    if (!afiliado?.correo?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Campo requerido', text: 'El correo es obligatorio.' });
      return false;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(afiliado.correo)) {
      Swal.fire({ icon: 'warning', title: 'Email inválido', text: 'Por favor ingresa un correo electrónico válido.' });
      return false;
    }

    if (!afiliado?.telefono?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Campo requerido', text: 'El teléfono es obligatorio.' });
      return false;
    }

    // Validar formato de teléfono (al menos 10 dígitos)
    const telefonoRegex = /^\+?[\d\s\-()]{10,}$/;
    if (!telefonoRegex.test(afiliado.telefono)) {
      Swal.fire({ icon: 'warning', title: 'Teléfono inválido', text: 'El teléfono debe tener al menos 10 dígitos.' });
      return false;
    }

    // Validar biografía
    if (!afiliado?.biografia?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Campo requerido', text: 'La biografía es obligatoria.' });
      return false;
    }

    if (afiliado.biografia.trim().length < 50) {
      Swal.fire({ icon: 'warning', title: 'Biografía muy corta', text: 'La biografía debe tener al menos 50 caracteres.' });
      return false;
    }

    // Validar años de experiencia
    const anosExp = Number(afiliado.años_experiencia || 0);
    if (anosExp < 0 || anosExp > 100) {
      Swal.fire({ icon: 'warning', title: 'Valor inválido', text: 'Los años de experiencia deben estar entre 0 y 100.' });
      return false;
    }

    // Validar calificación (0-100 en base de datos)
    const calificacion = Number(afiliado.calificacion || 0);
    if (calificacion < 0 || calificacion > 100) {
      Swal.fire({ icon: 'warning', title: 'Valor inválido', text: 'La calificación debe estar entre 0 y 100.' });
      return false;
    }

    return true;
  };

  const guardarCambios = async () => {
    if (!validarCampos()) return;
    try {
      await updateDoc(doc(db, 'asesor', id!), {
        nombre: afiliado.nombre.trim(),
        correo: afiliado.correo.trim().toLowerCase(),
        telefono: afiliado.telefono.trim(),
        biografia: afiliado.biografia.trim(),
        años_experiencia: Number(afiliado.años_experiencia || 0),
        calificacion: Number(afiliado.calificacion || 0),
        redes,
        // Mantener otros campos existentes
        ...(afiliado.foto && { foto: afiliado.foto }),
        ...(afiliado.especialidad && { especialidad: afiliado.especialidad }),
        ...(afiliado.sitio_web && { sitio_web: afiliado.sitio_web })
      });
      Swal.fire({ icon: 'success', title: 'Guardado', text: 'Cambios guardados correctamente.' })
        .then(() => navigate('/admin-afiliado'));
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar el afiliado.' });
    }
  };

  const eliminarAfiliado = async () => {
    const result = await Swal.fire({
      title: '¿Eliminar afiliado?',
      text: 'Esta acción es irreversible. También se eliminarán todas las referencias a este afiliado.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'asesor', id!));
        Swal.fire('Eliminado', 'El afiliado fue eliminado correctamente.', 'success');
        navigate('/admin-afiliado');
      } catch (error) {
        console.error('Error eliminando afiliado:', error);
        Swal.fire('Error', 'No se pudo eliminar el afiliado.', 'error');
      }
    }
  };

  if (!afiliado) return <div className="container py-5">Cargando afiliado...</div>;

  return (
    <>
      <Navbar transparent={false} />
      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Editar Afiliado</h2>
          <span className="ipn-subtitle">Actualiza los datos del afiliado</span>
        </div>
      </div>

      <section className="py-5">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Administración de Afiliados', path: '/admin-afiliado' },
              { label: afiliado?.nombre || 'Editar' }
            ]}
          />
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form className="taller-form-modern">
                  {/* Sección 1: Información Básica */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Información Básica</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Nombre <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            name="nombre"
                            value={afiliado.nombre || ''}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Ej. Juan Pérez"
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Correo <span className="text-danger">*</span></label>
                          <input
                            type="email"
                            name="correo"
                            value={afiliado.correo || ''}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="correo@dominio.com"
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Teléfono <span className="text-danger">*</span></label>
                          <input
                            type="tel"
                            name="telefono"
                            value={afiliado.telefono || ''}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="+52 322 123 4567"
                            required
                          />
                          <small className="form-text text-muted">Mínimo 10 dígitos</small>
                        </div>
                        <div className="form-group col-md-6">
                          <label>Años de experiencia <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            name="años_experiencia"
                            value={afiliado.años_experiencia || 0}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Ej. 5"
                            min="0"
                            max="100"
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Calificación <small className="text-muted">(escala 0-100, se muestra como 0-5)</small></label>
                          <input
                            type="number"
                            name="calificacion"
                            value={afiliado.calificacion || 0}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="0-100"
                            min="0"
                            max="100"
                            step="1"
                          />
                          <small className="form-text text-muted">
                            Valor actual: {afiliado.calificacion ? `${(afiliado.calificacion / 20).toFixed(1)}/5` : '0/5'}
                          </small>
                        </div>
                        <div className="form-group col-md-12">
                          <label>Biografía <span className="text-danger">*</span></label>
                          <textarea
                            name="biografia"
                            value={afiliado.biografia || ''}
                            onChange={handleChange}
                            className="form-control h-120"
                            placeholder="Perfil profesional del afiliado (mínimo 50 caracteres)"
                            required
                            minLength={50}
                          />
                          <small className="form-text text-muted">
                            Caracteres: {afiliado.biografia?.length || 0} {(afiliado.biografia?.length || 0) < 50 && '(mínimo 50)'}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Redes Sociales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Redes Sociales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Facebook</label>
                          <input type="text" name="facebook" value={redes.facebook} onChange={handleRedSocialChange} className="form-control" placeholder="https://facebook.com/..." />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Instagram</label>
                          <input type="text" name="instagram" value={redes.instagram} onChange={handleRedSocialChange} className="form-control" placeholder="https://instagram.com/..." />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Twitter</label>
                          <input type="text" name="twitter" value={redes.twitter} onChange={handleRedSocialChange} className="form-control" placeholder="https://twitter.com/..." />
                        </div>
                        <div className="form-group col-md-6">
                          <label>LinkedIn</label>
                          <input type="text" name="linkedin" value={redes.linkedin} onChange={handleRedSocialChange} className="form-control" placeholder="https://linkedin.com/in/..." />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="mt-4">
                    <div className="row">
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-success px-4"
                              onClick={guardarCambios}
                            >
                              <i className="fa fa-save me-2"></i>
                              Guardar Cambios
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary px-4"
                              onClick={() => navigate('/admin-afiliado')}
                            >
                              <i className="fa fa-times me-2"></i>
                              Cancelar
                            </button>
                          </div>
                          <div>
                            <button
                              type="button"
                              className="btn btn-danger px-4"
                              onClick={eliminarAfiliado}
                            >
                              <i className="fa fa-trash me-2"></i>
                              Eliminar Afiliado
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Información de propiedades asociadas */}
                    {propiedades.length > 0 && (
                      <div className="alert alert-info mt-4">
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>Nota:</strong> Este afiliado tiene {propiedades.length} propiedad(es) asociada(s).
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
